import { Audit, AuditEventEnum, IActivityLayout, useAuditsEvents } from '@diana/core';
import { IWorkAuditProps } from './WorkAudits.types';
import { WORK_PAGE_NS } from '../../i18n';
import { WorkAuditDataChangeDetails } from './WorkAuditDataChangeDetails';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const WorkAudit = (props: IWorkAuditProps) => {
  const { workId, audit, keyword, flow } = props;
  const { t } = useTranslation(WORK_PAGE_NS);

  const recipients = useMemo(() => {
    if (audit.activity.taskUsers) {
      return audit.activity.taskUsers?.filter(Boolean);
    }

    return [];
  }, [audit.activity.taskUsers]);

  const {
    createTextSummaryLayout,
    createInvitationLayout,
    createUpdateDataLayout,
    createPermissionsLayout,
    createCommentLayout,
  } = useAuditsEvents();

  const { verb, object, details } = useMemo((): IActivityLayout => {
    switch (audit.activity.type) {
      case AuditEventEnum.CREATED:
        return createTextSummaryLayout({
          audit,
          verb: t('activity.verbs.created'),
          data: audit.activity.taskName ?? '',
        });

      case AuditEventEnum.COMPLETED:
        return createTextSummaryLayout({
          audit,
          verb: t('activity.verbs.completed'),
          data: audit.activity.taskName ?? '',
        });

      case AuditEventEnum.SUBMITTED:
        return createInvitationLayout({
          audit,
          verb: t('activity.verbs.submitted'),
          data: {
            recipients,
            message: audit.activity.message ?? '',
          },
        });

      case AuditEventEnum.SENT_BACK:
        return createInvitationLayout({
          audit,
          verb: t('activity.verbs.sentBack'),
          data: {
            recipients,
            message: audit.activity.message ?? '',
          },
        });

      case AuditEventEnum.SKIPPED:
        return createTextSummaryLayout({
          audit,
          verb: t('activity.verbs.skipped'),
          data: audit.activity.taskName ?? '',
        });

      case AuditEventEnum.DECLINED:
        return createTextSummaryLayout({
          audit,
          verb: t('activity.verbs.declined'),
          data: audit.activity.taskName ?? '',
        });

      case AuditEventEnum.DELETED:
        return createTextSummaryLayout({
          audit,
          verb: t('activity.verbs.deleted'),
          data: audit.activity.taskName ?? '',
        });

      case AuditEventEnum.NEW_WORK_ITEM_GENERATED:
        return createTextSummaryLayout({
          audit,
          verb: t('activity.verbs.generatedWorkItem'),
          data: audit.activity.taskName ?? '',
        });

      case AuditEventEnum.EDITED:
        return createUpdateDataLayout({
          audit,
          verb: t('activity.verbs.edited'),
          data: [],
          detailsComponent: (
            <WorkAuditDataChangeDetails
              keyword={keyword}
              workId={workId}
              flow={flow}
              audit={audit}
            />
          ),
        });

      case AuditEventEnum.UPDATED_PERMISSION:
        return createPermissionsLayout({
          audit,
          verb: t('activity.verbs.updatedPermissions'),
          data: [],
        });

      case AuditEventEnum.COMMENTED:
        return createCommentLayout({
          audit,
          verb: t('activity.verbs.commented'),
          data: audit.activity.message ?? '',
        });

      default:
        return createTextSummaryLayout({ audit, verb: '[unknown event]' });
    }
  }, [
    audit,
    createTextSummaryLayout,
    workId,
    t,
    createInvitationLayout,
    recipients,
    createUpdateDataLayout,
    keyword,
    flow,
    createPermissionsLayout,
    createCommentLayout,
  ]);

  return <Audit audit={audit} details={details} verb={verb} object={object} />;
};
